/**
 * usePaymentIntent — create a Stripe PaymentIntent / Subscription via the
 * in-app checkout flow.
 *
 * in-app-checkout Phase 3 Session 3.3.
 *
 * Calls `POST /api/billing/subscription` which creates a Stripe Subscription
 * with `payment_behavior: 'default_incomplete'` and returns the associated
 * PaymentIntent `client_secret`. The web side then confirms the PaymentIntent
 * inline via Stripe's `<PaymentElement>` + `confirmPayment()` — no redirect
 * to Stripe-hosted Checkout.
 *
 * The mutation is LAZY (no auto-fire on mount) — callers invoke
 * `createSubscription({ tier_id, conversation? })` when the user clicks
 * "Subscribe" on the order-review step.
 *
 * Error handling:
 *   - Returns `{ clientSecret, status }` on success so the caller can call
 *     `confirmPayment`.
 *   - Throws `ApiError` on HTTP error (rate-limiting, invalid tier, already
 *     subscribed). The component maps these to `BILLING_ERROR_COPY`.
 *
 * Architecture note:
 *   - This hook does NOT call `useAuth` internally. `enabled` is controlled by
 *     the caller (the component mounts only when the user is approved).
 *   - Cache key is `queryKeys.billing.paymentIntent(tierId)` so parallel
 *     requests for different tiers are isolated, and an in-flight intent for
 *     the same tier is not re-requested.
 *   - `retry: 0` — payment mutations must not auto-retry (idempotency risk).
 */

import { useMutation } from '@tanstack/react-query';
import type { PaymentIntentResponse } from '@arcanum-hq/shared-types';
import { api } from '@/lib/api';

export type { PaymentIntentResponse };

interface CreateSubscriptionInput {
  /** The tier to subscribe to (e.g. `'principal'`, `'sovereign'`). */
  tier_id: string;
  /**
   * Optional deep-link conversation id — when set, post-subscribe nav lands
   * on `/builder?conversation=<id>&tab=workbench` instead of `/cockpit`.
   * Mirrors the `conversation_share_id` field in `checkoutSessionRequestSchema`
   * (shared-types billing.types.ts:361-367).
   */
  conversation?: string;
}

interface SubscriptionEnvelope {
  data: PaymentIntentResponse;
}

/**
 * useMutation hook for creating a Stripe subscription (in-app checkout).
 *
 * Returns the shared-types discriminated union:
 *   `{ status: 'succeeded', subscription_id }` — payment already completed
 *     server-side (no 3DS needed); caller should fire checkout_completed + onSuccess.
 *   `{ status: 'requires_action', client_secret }` — 3DS/SCA needed; caller
 *     renders PaymentElement and calls confirmPayment with client_secret.
 *
 * Usage:
 * ```tsx
 * const { mutateAsync: createSubscription, isPending, error } = usePaymentIntent();
 * const result = await createSubscription({ tier_id: 'principal' });
 * if (result.status === 'succeeded') { onSuccess(); } else { setClientSecret(result.client_secret); }
 * ```
 */
export function usePaymentIntent() {
  return useMutation<PaymentIntentResponse, Error, CreateSubscriptionInput>({
    mutationFn: async ({ tier_id, conversation }) => {
      const envelope = await api.post<SubscriptionEnvelope>(
        '/api/billing/subscription',
        {
          tier_id,
          ...(conversation ? { conversation } : {}),
        }
      );
      return envelope.data;
    },
    // Never auto-retry payment mutations — idempotency risk.
    retry: 0,
  });
}
